import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MobileShell, C, MobileHeader, MobileSheet} from './MobileShell';
import mapi from './api';
import { ChevronRight, ChevronLeft, Video, X } from 'lucide-react';
import { formatClocks, formatTime } from '@/lib/utils';
import { fetchAttendanceCategories, presenceCategories, consequenceText } from './attendanceCategories';
import { resolveAttendanceStatus, STATUS_PALETTE, todayIsoLocal } from '@/lib/attendanceStatus';

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const CYCLE = { null: 'present', present: 'absent_excused', absent_excused: 'absent_unexcused', absent_unexcused: null };

const nowMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const shiftMonth = (ym, delta) => {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const fmtDate = (iso) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.`;

export default function MobileAttendance() {
  const [courses, setCourses] = useState(null);
  const [selected, setSelected] = useState(null);
  const [month, setMonth] = useState(nowMonth());
  const [matrix, setMatrix] = useState(null);
  const [activeDate, setActiveDate] = useState(null);
  const [joinUrl, setJoinUrl] = useState('');

  useEffect(() => {
    mapi.get('/attendance-lists/courses')
      .then((r) => setCourses(r.data.courses || []))
      .catch(() => setCourses([]));
  }, []);

  useEffect(() => {
    if (!selected) { setMatrix(null); return; }
    mapi.get(`/attendance-lists/courses/${selected.id}`, { params: { month } })
      .then((r) => {
        setMatrix(r.data);
        const today = new Date().toISOString().slice(0, 10);
        const ds = r.data.dates || [];
        setActiveDate(ds.includes(today) ? today : (ds.filter((d) => d <= today).slice(-1)[0] || ds[0] || null));
      })
      .catch(() => setMatrix(null));
  }, [selected, month]);

  const monthLabel = useMemo(() => {
    const [y, m] = month.split('-').map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
  }, [month]);

  const [error, setError] = useState('');
  const onlineNow = !!(matrix?.course?.is_online || (activeDate && matrix?.online_dates?.[activeDate]));

  useEffect(() => {
    if (!selected || !onlineNow) {
      setJoinUrl('');
      return undefined;
    }
    let cancelled = false;
    mapi.get(`/online-lessons/courses/${selected.id}/join`)
      .then((r) => { if (!cancelled) setJoinUrl(r.data?.url || ''); })
      .catch(() => { if (!cancelled) setError('Online-Raum konnte nicht geladen werden.'); });
    return () => { cancelled = true; };
  }, [selected, onlineNow]);

  const [categories, setCategories] = useState([]);
  const [pickStudent, setPickStudent] = useState(null);

  useEffect(() => {
    if (!selected) { setCategories([]); return; }
    fetchAttendanceCategories(selected.id).then(setCategories);
  }, [selected]);

  const presenceOpts = useMemo(() => presenceCategories(categories), [categories]);

  const applyMark = async (studentId, { status, category_code } = {}) => {
    const existing = matrix?.marks?.[`${activeDate}|${studentId}`];
    if (existing?.is_finalized) {
      setError('Finalisiert — nicht mehr per Tipp änderbar.');
      setPickStudent(null);
      return;
    }
    try {
      const r = await mapi.post(`/attendance-lists/courses/${selected.id}/mark`, {
        lesson_date: activeDate,
        student_id: studentId,
        ...(category_code ? { category_code } : { status: status ?? 'none' }),
      });
      setError('');
      setPickStudent(null);
      setMatrix((m) => {
        const marks = { ...m.marks };
        const key = `${activeDate}|${studentId}`;
        if (r.data.category_code || r.data.status) {
          marks[key] = {
            status: r.data.status,
            category_code: r.data.category_code,
            is_finalized: !!r.data.is_finalized,
            is_reported: !!r.data.is_reported,
          };
        } else {
          delete marks[key];
        }
        return {
          ...m, marks,
          students: m.students.map((s) => (s.student_id === studentId ? { ...s, prepaid: r.data.prepaid } : s)),
        };
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(detail || 'Speichern fehlgeschlagen — bitte erneut versuchen.');
    }
  };

  const mark = async (studentId, current, currentMark) => {
    if (currentMark?.is_finalized) {
      setError('Finalisiert — nicht mehr per Tipp änderbar.');
      return;
    }
    if (presenceOpts.length) {
      setPickStudent(studentId);
      return;
    }
    const next = CYCLE[current ?? null];
    await applyMark(studentId, { status: next ?? 'none' });
  };

  const finalizeMonth = async () => {
    if (!selected) return;
    if (!window.confirm('Erfasste Einträge dieses Monats finalisieren? Offene Termine bleiben offen.')) return;
    try {
      const r = await mapi.post(`/attendance-lists/courses/${selected.id}/finalize`, { month });
      const n = r.data?.finalized || 0;
      setError('');
      setMatrix(null);
      const fresh = await mapi.get(`/attendance-lists/courses/${selected.id}`, { params: { month } });
      setMatrix(fresh.data);
      const today = new Date().toISOString().slice(0, 10);
      const ds = fresh.data.dates || [];
      setActiveDate(ds.includes(today) ? today : (ds.filter((d) => d <= today).slice(-1)[0] || ds[0] || null));
      if (!n) setError('Keine erfassten Einträge zum Finalisieren.');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Finalisieren fehlgeschlagen.');
    }
  };

  return (
    <MobileShell>
      <MobileHeader title="Anwesenheit" backTo="/m" />
      <div className="flex-1 px-4 pb-8 space-y-3">
        {selected && (
          <button onClick={() => { setSelected(null); setMatrix(null); }}
                  data-testid="mobile-attendance-back-to-courses"
                  className="text-xs font-semibold mt-1" style={{ color: C.accent }}>
            ‹ Zur Kursliste
          </button>
        )}
        {!selected ? (
          <>
            <p className="text-xs mt-1" style={{ color: C.dim }}>Wähle einen Kurs, um die Anwesenheit zu pflegen.</p>
            {courses === null && <p className="text-sm" style={{ color: C.dim }}>Laden...</p>}
            {courses?.length === 0 && <p className="text-sm" style={{ color: C.dim }}>Keine Kurse gefunden.</p>}
            {(courses || []).map((c) => (
              <button key={c.id} onClick={() => setSelected(c)}
                      data-testid={`mobile-attendance-course-${c.id}`}
                      className="w-full flex items-center gap-3 rounded-2xl px-4 py-3 text-left"
                      style={{ background: C.card, border: `1px solid ${C.border}` }}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate" style={{ color: C.text }}>
                    {formatClocks(c.name) || c.name}
                    {c.is_online && (
                      <span className="ml-2 text-[9px] font-bold px-1.5 py-0.5 rounded align-middle"
                            style={{ background: '#065f46', color: '#6ee7b7' }} data-testid={`mobile-online-tag-${c.id}`}>
                        ONLINE
                      </span>
                    )}
                  </div>
                  <div className="text-[11px]" style={{ color: C.dim }}>
                    {[c.day_of_week != null ? WEEKDAYS[c.day_of_week] : null, formatTime(c.start_time), `${c.student_count} Schüler`].filter(Boolean).join(' · ')}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4" style={{ color: C.dim }} />
              </button>
            ))}
          </>
        ) : (
          <>
            <div className="rounded-2xl p-3" style={{ background: C.card, border: `1px solid ${C.border}` }}>
              <div className="text-sm font-semibold" style={{ color: C.text }}>{selected.name}</div>
              {onlineNow && (
                joinUrl ? (
                  <a href={joinUrl} target="_blank" rel="noopener noreferrer"
                     data-testid="mobile-join-online-btn"
                     className="w-full flex items-center justify-center gap-2 rounded-xl px-3 py-2.5 mt-2 text-sm font-semibold"
                     style={{ background: '#059669', color: '#ffffff' }}>
                    <Video className="w-4 h-4" /> Onlineunterricht beitreten
                  </a>
                ) : (
                  <button type="button" disabled data-testid="mobile-join-online-btn"
                          className="w-full flex items-center justify-center gap-2 rounded-xl px-3 py-2.5 mt-2 text-sm font-semibold"
                          style={{ background: '#059669', color: '#ffffff', opacity: 0.7 }}>
                    <Video className="w-4 h-4" /> Online-Raum wird vorbereitet
                  </button>
                )
              )}
              {onlineNow && joinUrl && (
                <p className="text-[11px] mt-1" style={{ color: C.dim }}>
                  Der Videoraum öffnet sich im Browser.
                </p>
              )}
              <div className="flex items-center justify-between mt-2">
                <button onClick={() => setMonth(shiftMonth(month, -1))} className="p-1" data-testid="mobile-attendance-prev-month">
                  <ChevronLeft className="w-4 h-4" style={{ color: C.dim }} />
                </button>
                <span className="text-xs font-medium" style={{ color: C.text }}>{monthLabel}</span>
                <button onClick={() => setMonth(shiftMonth(month, 1))} className="p-1" data-testid="mobile-attendance-next-month">
                  <ChevronRight className="w-4 h-4" style={{ color: C.dim }} />
                </button>
              </div>
              {matrix?.can_write !== false && (
                <button
                  type="button"
                  onClick={finalizeMonth}
                  data-testid="att-bulk-finalize"
                  className="w-full mt-2 rounded-xl px-3 py-2 text-xs font-semibold"
                  style={{ background: C.bg, color: C.text, border: `1px solid ${C.border}` }}
                >
                  Einträge finalisieren
                </button>
              )}
              {matrix && (
                <div className="flex gap-1.5 mt-2 overflow-x-auto pb-1">
                  {matrix.dates.map((d) => (
                    <button key={d} onClick={() => setActiveDate(d)}
                            data-testid={`mobile-attendance-date-${d}`}
                            className="px-2.5 py-1.5 rounded-full text-[11px] font-semibold shrink-0"
                            style={activeDate === d
                              ? { background: C.accent, color: '#0b0f14' }
                              : { background: 'transparent', color: C.dim, border: `1px solid ${C.border}` }}>
                      {fmtDate(d)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {matrix && activeDate && (
              <div className="space-y-2">
                {error && (
                  <div className="rounded-xl px-3 py-2 text-xs" data-testid="mobile-attendance-error"
                       style={{ background: '#7f1d1d', color: '#fca5a5' }}>{error}</div>
                )}
                {matrix.students.map((s) => {
                  const m = matrix.marks[`${activeDate}|${s.student_id}`];
                  const locked = !!m?.is_finalized;
                  const resolved = resolveAttendanceStatus({
                    category_code: m?.category_code,
                    status: m?.status,
                    lesson_date: activeDate,
                    today: todayIsoLocal(),
                  });
                  const pal = resolved ? (STATUS_PALETTE[resolved.code] || STATUS_PALETTE.offen) : null;
                  const ui = resolved
                    ? { label: resolved.short, title: resolved.title, bg: pal.bg, fg: pal.fg }
                    : null;
                  return (
                    <div key={s.student_id} className="flex items-center gap-3 rounded-2xl px-4 py-3"
                         style={{ background: C.card, border: `1px solid ${C.border}` }}>
                      <div className="flex-1 min-w-0">
                        <Link
                          to={`/students/${s.student_id}`}
                          className="text-sm font-medium truncate block hover:underline"
                          style={{ color: C.text }}
                          data-testid={`mobile-attendance-student-link-${s.student_id}`}
                        >
                          {s.name}
                        </Link>
                        {s.prepaid && (
                          <div className="text-[10px]" style={{ color: s.prepaid.used >= s.prepaid.total ? '#fca5a5' : C.dim }}>
                            Karte: {s.prepaid.used}/{s.prepaid.total}
                            {s.prepaid.remaining != null ? ` · ${s.prepaid.remaining} übrig` : ''} Einheiten
                          </div>
                        )}
                      </div>
                      <button onClick={() => mark(s.student_id, m?.status ?? null, m)}
                              disabled={locked}
                              data-testid={`mobile-attendance-toggle-${s.student_id}`}
                              title={locked ? 'Finalisiert — nicht mehr per Tipp änderbar' : (ui?.title || 'Markieren')}
                              className="w-11 h-11 rounded-xl text-base font-bold"
                              style={ui
                                ? { background: ui.bg, color: ui.fg, border: `1px solid ${ui.fg}44`, opacity: locked ? 0.7 : 1 }
                                : { background: 'transparent', color: C.dim, border: `1px dashed ${C.border}` }}>
                        {ui?.label || '·'}
                      </button>
                    </div>
                  );
                })}
                <p className="text-[10px] text-center pt-1" style={{ color: C.dim }}>
                  {presenceOpts.length
                    ? 'Tippen öffnet die Ausfall- und Anwesenheitsarten des Mandanten'
                    : 'Tippen wechselt: leer → ✓ Anwesend → E Entschuldigt → U Unentschuldigt → leer'}
                </p>
              </div>
            )}
          </>
        )}
      </div>
      {pickStudent && (
        <MobileSheet className="fixed inset-0 z-50 flex items-end justify-center" style={{ background: 'rgba(0,0,0,0.45)' }}
             onClick={() => setPickStudent(null)} data-testid="mobile-attendance-category-sheet">
          <div className="w-full max-w-md rounded-t-2xl p-4 space-y-2 max-h-[80vh] overflow-y-auto"
               style={{ background: C.card }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <p className="text-sm font-bold">Anwesenheitsart</p>
              <button type="button" onClick={() => setPickStudent(null)} aria-label="Schließen">
                <X className="w-5 h-5" style={{ color: C.dim }} />
              </button>
            </div>
            {presenceOpts.map((cat) => (
              <button
                key={cat.code}
                type="button"
                onClick={() => applyMark(pickStudent, { category_code: cat.code })}
                data-testid={`mobile-attendance-cat-${cat.code}`}
                className="w-full text-left rounded-xl px-3 py-3"
                style={{ background: C.bg, border: `1px solid ${C.border}` }}
              >
                <span className="block text-sm font-semibold">{cat.label}</span>
                <span className="block text-[11px] mt-0.5" style={{ color: C.dim }}>{consequenceText(cat)}</span>
              </button>
            ))}
            <button type="button" onClick={() => applyMark(pickStudent, { status: 'none' })}
                    data-testid="mobile-attendance-cat-none"
                    className="w-full text-left rounded-xl px-3 py-3 text-sm"
                    style={{ color: C.dim }}>
              Eintrag löschen
            </button>
          </div>
        </MobileSheet>
      )}
    </MobileShell>
  );
}
